import { Helmet } from "react-helmet-async";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import WhatsAppButton from "@/components/WhatsAppButton";
import { useLanguage } from "@/contexts/LanguageContext";
import { useSiteContent, pickLocalized } from "@/contexts/SiteContentContext";
import { absoluteUrl, buildHreflangAlternates, SITE_NAME } from "@/lib/seo";

const FALLBACK = {
  company: "WISETEC69 LLC.",
  address_line1: "30 N Gould St Sten",
  address_line2: "82801 Sheridan",
  address_country: "Wyoming/USA",
  phone: "+1 (716) 755-7656",
  email: "info@digital-mallorca.de",
  mallorca_support: "+34 657 13 89 15",
  responsible: "WISETEC69 LLC.",
  service_area: "Mallorca · Inselweiter Service",
};

const HEADINGS = {
  de: {
    title: "Impressum",
    subtitle: "Angaben gemäß §5 TMG",
    company: "Anbieter",
    contact: "Kontakt",
    support: "Support / Technik Mallorca",
    whatsapp: "WhatsApp Mallorca",
    responsibility: "Verantwortlich für den Inhalt nach §55 Abs. 2 RStV",
    serviceArea: "Servicegebiet",
    eu_dispute: "EU-Streitschlichtung",
    eu_dispute_body:
      "Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung (OS) bereit:",
    consumer_dispute: "Verbraucherstreitbeilegung / Universalschlichtungsstelle",
    consumer_dispute_body:
      "Wir sind nicht bereit oder verpflichtet, an Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle teilzunehmen.",
    liability_title: "Haftungsausschluss",
    liability_body:
      "Die Inhalte unserer Seiten wurden mit größter Sorgfalt erstellt. Für die Richtigkeit, Vollständigkeit und Aktualität der Inhalte können wir jedoch keine Gewähr übernehmen. Als Diensteanbieter sind wir gemäß §7 Abs. 1 TMG für eigene Inhalte auf diesen Seiten nach den allgemeinen Gesetzen verantwortlich. Nach §§8 bis 10 TMG sind wir als Diensteanbieter jedoch nicht verpflichtet, übermittelte oder gespeicherte fremde Informationen zu überwachen.",
    copyright_title: "Urheberrecht",
    copyright_body:
      "Die durch die Seitenbetreiber erstellten Inhalte und Werke auf diesen Seiten unterliegen dem deutschen Urheberrecht. Die Vervielfältigung, Bearbeitung, Verbreitung und jede Art der Verwertung außerhalb der Grenzen des Urheberrechtes bedürfen der schriftlichen Zustimmung des jeweiligen Autors bzw. Erstellers.",
  },
  en: {
    title: "Legal Notice",
    subtitle: "Information pursuant to §5 TMG",
    company: "Provider",
    contact: "Contact",
    support: "Support / Tech Mallorca",
    whatsapp: "WhatsApp Mallorca",
    responsibility: "Responsible for content according to §55 Abs. 2 RStV",
    serviceArea: "Service area",
    eu_dispute: "EU dispute resolution",
    eu_dispute_body:
      "The European Commission provides a platform for online dispute resolution (ODR):",
    consumer_dispute: "Consumer dispute resolution",
    consumer_dispute_body:
      "We are not willing or obliged to participate in dispute resolution proceedings before a consumer arbitration board.",
    liability_title: "Disclaimer",
    liability_body:
      "The contents of our pages have been created with the utmost care. However, we cannot guarantee the accuracy, completeness, and timeliness of the content. As a service provider, we are responsible for our own content on these pages in accordance with general laws (§7 (1) TMG). According to §§8 to 10 TMG, however, we are not obliged as a service provider to monitor transmitted or stored third-party information.",
    copyright_title: "Copyright",
    copyright_body:
      "The contents and works created by the site operators on these pages are subject to German copyright law. Duplication, processing, distribution, and any form of commercialization beyond the limits of copyright require written consent from the respective author or creator.",
  },
  es: {
    title: "Aviso Legal",
    subtitle: "Información según §5 TMG",
    company: "Proveedor",
    contact: "Contacto",
    support: "Soporte técnico Mallorca",
    whatsapp: "WhatsApp Mallorca",
    responsibility: "Responsable del contenido según §55 Abs. 2 RStV",
    serviceArea: "Área de servicio",
    eu_dispute: "Resolución de conflictos UE",
    eu_dispute_body:
      "La Comisión Europea proporciona una plataforma para la resolución de conflictos en línea (ODR):",
    consumer_dispute: "Resolución de conflictos de consumo",
    consumer_dispute_body:
      "No estamos dispuestos ni obligados a participar en procedimientos de resolución de conflictos ante un órgano de arbitraje de consumo.",
    liability_title: "Exención de responsabilidad",
    liability_body:
      "Los contenidos de nuestras páginas se han elaborado con el máximo cuidado. No obstante, no podemos garantizar la exactitud, integridad y actualidad de los contenidos.",
    copyright_title: "Derechos de autor",
    copyright_body:
      "Los contenidos y obras creados por los operadores del sitio en estas páginas están sujetos a la legislación alemana sobre derechos de autor.",
  },
};

const Imprint = () => {
  const { locale } = useLanguage();
  const { content } = useSiteContent();
  const t = HEADINGS[locale] || HEADINGS.de;
  const legal = content?.legal?.imprint || {};
  const get = (k) => legal[k] || FALLBACK[k];

  const canonicalUrl = absoluteUrl("/impressum");
  const alternates = buildHreflangAlternates("/impressum");

  return (
    <div data-testid="page-imprint" className="min-h-screen bg-[#fdfbf7] text-neutral-900">
      <Helmet>
        <html lang={locale} />
        <title>{`${t.title} · ${SITE_NAME}`}</title>
        <meta name="description" content={`${t.title} – ${t.subtitle}`} />
        <link rel="canonical" href={canonicalUrl} />
        {alternates.map((a) => (
          <link key={a.hrefLang} rel="alternate" hrefLang={a.hrefLang} href={a.href} />
        ))}
        <meta name="robots" content="index, follow" />
      </Helmet>
      <Navigation />
      <main className="pt-32 lg:pt-40 pb-24">
        <div className="container-px mx-auto max-w-3xl">
          <div className="text-xs uppercase tracking-[0.22em] text-sky-700 font-medium">
            {t.subtitle}
          </div>
          <h1 className="font-display mt-3 text-4xl lg:text-6xl font-light tracking-tighter">
            {t.title}
          </h1>

          <section className="mt-12 space-y-2">
            <div className="text-xs uppercase tracking-[0.18em] text-neutral-500">{t.company}</div>
            <div className="text-lg font-medium">{get("company")}</div>
            <div className="text-neutral-700 leading-relaxed">
              {get("address_line1")}<br />
              {get("address_line2")}<br />
              {get("address_country")}
            </div>
          </section>

          <section className="mt-10 space-y-2">
            <div className="text-xs uppercase tracking-[0.18em] text-neutral-500">{t.contact}</div>
            <div className="text-neutral-700 leading-relaxed">
              Phone: <a className="text-sky-700 hover:underline" href={`tel:${get("phone").replace(/\s/g, "")}`}>{get("phone")}</a><br />
              E-Mail: <a className="text-sky-700 hover:underline" href={`mailto:${get("email")}`}>{get("email")}</a>
            </div>
          </section>

          <section className="mt-10 space-y-2">
            <div className="text-xs uppercase tracking-[0.18em] text-neutral-500">{t.support}</div>
            <div className="text-neutral-700">{get("service_area")}</div>
            <div className="text-neutral-700">
              {t.whatsapp}: <a className="text-sky-700 hover:underline" href={`https://wa.me/${get("mallorca_support").replace(/[^+\d]/g, "").replace(/^\+/, "")}`} target="_blank" rel="noreferrer">{get("mallorca_support")}</a>
            </div>
          </section>

          <section className="mt-10 space-y-2">
            <div className="text-xs uppercase tracking-[0.18em] text-neutral-500">{t.responsibility}</div>
            <div className="text-neutral-700">{get("responsible")}</div>
          </section>

          <section className="mt-12 pt-8 border-t border-neutral-200 space-y-3">
            <h2 className="font-display text-2xl font-medium">{t.eu_dispute}</h2>
            <p className="text-neutral-700 leading-relaxed">
              {t.eu_dispute_body}{" "}
              <a href="https://ec.europa.eu/consumers/odr/" target="_blank" rel="noreferrer" className="text-sky-700 hover:underline">
                https://ec.europa.eu/consumers/odr/
              </a>
            </p>
          </section>

          <section className="mt-10 space-y-3">
            <h2 className="font-display text-2xl font-medium">{t.consumer_dispute}</h2>
            <p className="text-neutral-700 leading-relaxed">{t.consumer_dispute_body}</p>
          </section>

          <section className="mt-10 space-y-3">
            <h2 className="font-display text-2xl font-medium">{t.liability_title}</h2>
            <p className="text-neutral-700 leading-relaxed">{t.liability_body}</p>
          </section>

          <section className="mt-10 space-y-3">
            <h2 className="font-display text-2xl font-medium">{t.copyright_title}</h2>
            <p className="text-neutral-700 leading-relaxed">{t.copyright_body}</p>
          </section>
        </div>
      </main>
      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default Imprint;
